/**
 * PROCESSES SERVICE - Infraestructura Base
 * 
 * Sistema avanzado de gestión de procesos
 */

import { BaseService } from './base.service';
import { db, functions } from '@/core/config/firebase';
import { Collections } from '@/core/config/collections';

import type { ProcessDefinition, ProcessExecution } from '../types/models/process.model';

export class ProcessesService extends BaseService<ProcessDefinition> {
  constructor() {
    super('process_definitions', db);
  }

  /**
   * Ejecutar proceso manualmente
   */
  async executeProcess(processId: string): Promise<ProcessExecution> {
    this.logger.info('Executing process manually', { processId });
    
    const process = await this.getById(processId);
    if (!process) {
      throw new Error('Process not found');
    }

    const execution: Omit<ProcessExecution, 'id'> = {
      processId,
      processName: process.displayName,
      status: 'pending',
      createdAt: new Date().toISOString(),
      attempt: 1,
      maxAttempts: process.execution.retryPolicy.maxRetries,
      triggeredBy: this.getCurrentUserId() || 'manual'
    };

    const execRef = await db.collection(Collections.PROCESS_EXECUTIONS).add(execution);

    this.logger.info('Process execution created', { 
      executionId: execRef.id,
      processName: process.displayName
    });

    // Actualizar stats
    await this.update(processId, {
      'stats.totalRuns': (process.stats.totalRuns || 0) + 1,
      'stats.lastRun': new Date().toISOString()
    } as any);

    return {
      id: execRef.id,
      ...execution
    };
  }

  /**
   * Pausar proceso
   */
  async pauseProcess(processId: string): Promise<void> {
    this.logger.info('Pausing process', { processId });
    await this.update(processId, { paused: true } as any);
  }

  /**
   * Reanudar proceso
   */
  async resumeProcess(processId: string): Promise<void> {
    this.logger.info('Resuming process', { processId });
    await this.update(processId, { paused: false } as any);
  }

  /**
   * Obtener ejecuciones de un proceso
   */
  async getExecutions(processId: string, limit = 50): Promise<ProcessExecution[]> {
    const snapshot = await db
      .collection('process_executions')
      .where('processId', '==', processId)
      .orderBy('createdAt', 'desc')
      .limit(limit)
      .get();

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as ProcessExecution[];
  }

  /**
   * Obtener procesos activos
   */
  async getActiveProcesses(): Promise<ProcessDefinition[]> {
    return this.query([
      ['active', '==', true],
      ['paused', '==', false]
    ]);
  }

  private getCurrentUserId(): string {
    // Primero intentar obtener del contexto global de Auth
    const userId = (window as any).__CURRENT_USER_ID__;
    if (userId) return userId;
    
    // Fallback: intentar obtener directamente de firebase
    const auth = (window as any).firebase?.auth();
    return auth?.currentUser?.uid || 'system';
  }
}

export const processesService = new ProcessesService();

